/**
 * useApprovalAccess — the Canonical_Approval_Reader
 * (approval-status-ssot-unification, Task 1 / Requirements 1, 6, 7).
 *
 * The single approval-status read path every approval-gating surface consumes.
 * Generalizes the Podium `useMessengerAccess` gate (which now delegates here) so
 * navigation, dashboards, profile gating, and admin surfaces all derive the same
 * approval state from the SAME source of truth and cannot diverge.
 *
 * Approval SOURCE (contestant mode): the caller's own
 * `ApplicationResponse.submissionStatus` for the current competition — the SSOT
 * the director review decision (`reviewApplicationResponse`) writes. NEVER the
 * legacy `ApplicantYearProfile.submissionStatus` / `listApplicantYearProfiles`
 * (R1.8, R1.9).
 *
 * Resolution (R1, R6):
 *   - unauthenticated                → 'hidden'  (precedence over role, R6.4)
 *   - judge                          → 'hidden'  (no store read, R6.3)
 *   - admin / poweruser (Privileged) → 'full'    (no store read, R6.1/R6.2)
 *   - contestant, APPROVED           → 'full'
 *   - contestant, anything else / absent / read-failure → 'pending_approval'
 *     (never falls back to the legacy store to grant, R8.6)
 *   - any other role                 → 'hidden'  (R1.10)
 * On read failure the state is 'pending_approval' AND `error` is set — a
 * distinct error indication from a resolved pending (R1.12).
 */

import { useCallback, useEffect, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from '@aws-amplify/api-graphql';
import { useAuth } from './useAuth';
import { useActiveTenantId } from '@/context/AuthContext';
import { useActiveSeason } from './useActiveSeason';
import { getBrandingConfig } from '@/graphql/queries';
import { applicationFormClient } from '@/lib/application-form/client';
import type { SubmissionStatus } from '@/lib/application-form/client';
import { resolveActiveCompetitionId } from '@/lib/application-form/activeCompetition';
import { useApprovalAccessInvalidation } from './approvalAccessInvalidation';

/** The sole value an approval-gating read may resolve to (R1.2). */
export type ApprovalAccessState = 'hidden' | 'pending_approval' | 'full';

export interface UseApprovalAccessResult {
  state: ApprovalAccessState;
  loading: boolean;
  /** Distinct error indication (R1.12) — set on SSOT read failure while state is `pending_approval`. */
  error: string | null;
}

// Module-scope cache for the current competition year (BrandingConfig sentinel).
// 5-minute TTL; the underlying row changes ~once per pageant cycle. Shared by
// every consumer of the canonical reader.
const BRANDING_TTL_MS = 5 * 60 * 1000;
let cachedYear: { value: number; fetchedAt: number } | null = null;

// Exposed for tests to reset the module cache between cases.
export function __resetApprovalYearCache(): void {
  cachedYear = null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function fetchCurrentCompetitionYear(client: any): Promise<number> {
  if (cachedYear && Date.now() - cachedYear.fetchedAt < BRANDING_TTL_MS) {
    return cachedYear.value;
  }
  const fallback = new Date().getUTCFullYear();
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const res = (await (client as any).graphql({
      query: getBrandingConfig,
      variables: { competitionYear: fallback },
    })) as GraphQLResult<{
      getBrandingConfig: { competitionYear?: number } | null;
    }>;
    const y = res.data?.getBrandingConfig?.competitionYear;
    if (typeof y === 'number') {
      cachedYear = { value: y, fetchedAt: Date.now() };
      return y;
    }
  } catch {
    // swallow — fall through to the deployed UTC year
  }
  cachedYear = { value: fallback, fetchedAt: Date.now() };
  return fallback;
}

export function useApprovalAccess(): UseApprovalAccessResult {
  const { role, user, isLoading: authLoading } = useAuth();
  // A successful Director_Decision_Action bumps this; it re-runs `compute` so
  // the reader re-reads the Approval_SSOT and reflects the new status within 5s
  // without a manual reload (Task 6, Req 5.5). The contestant branch sets
  // `loading` at the start of the re-read, so no stale unlocked state is shown
  // while it is in flight (Req 5.6), and a failed re-read resolves to
  // `pending_approval` + error (Req 5.7) via the existing catch.
  const invalidationVersion = useApprovalAccessInvalidation();
  const [state, setState] = useState<ApprovalAccessState>('hidden');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const compute = useCallback(async () => {
    // Unauthenticated → hidden (takes precedence over any role, R6.4).
    if (!user || authLoading) {
      setState('hidden');
      setLoading(authLoading);
      setError(null);
      return;
    }
    // Judge → hidden, no store read (R6.3).
    if (role === 'judge') {
      setState('hidden');
      setLoading(false);
      setError(null);
      return;
    }
    // Admin / PowerUser (Privileged_Role) → full, no store read (R6.1, R6.2).
    if (role === 'admin' || role === 'poweruser') {
      setState('full');
      setLoading(false);
      setError(null);
      return;
    }
    // Any non-contestant, non-privileged role → hidden (R1.10).
    if (role !== 'contestant') {
      setState('hidden');
      setLoading(false);
      setError(null);
      return;
    }
    // Contestant → approval from the Approval_SSOT for the current competition.
    setLoading(true);
    try {
      const client = generateClient();
      const currentYear = await fetchCurrentCompetitionYear(client);
      const competitionId = resolveActiveCompetitionId(currentYear, user?.activeTenantId);
      const response = await applicationFormClient.getMyApplicationResponse(competitionId);
      const approved = response?.submissionStatus === 'APPROVED';
      setState(approved ? 'full' : 'pending_approval');
      setError(null);
      setLoading(false);
    } catch (err) {
      // Conservative default (R8.6): never fall back to the legacy store to
      // grant — resolve pending_approval and surface a distinct error (R1.12).
      console.error('[useApprovalAccess] Failed to compute approval status:', err);
      setState('pending_approval');
      setError(err instanceof Error ? err.message : String(err));
      setLoading(false);
    }
  }, [role, user, authLoading, invalidationVersion]);

  useEffect(() => {
    compute();
  }, [compute]);

  return { state, loading, error };
}

// ===========================================================================
// Admin / director list mode — the Canonical_Approval_Reader for admin surfaces
// (approval-status-ssot-unification, Task 2 / Requirement 2).
// ===========================================================================

/**
 * The per-applicant approval lookup an admin/director surface consumes.
 *
 * Every value derives from a SINGLE `listResponses(competitionId)` read of the
 * Approval_SSOT (`ApplicationResponse.submissionStatus`) — never the legacy
 * `listApplicantYearProfiles` roster (R2.1, R2.5). The approval condition is
 * uniformly `submissionStatus === 'APPROVED'` (R2.2); an applicant with no
 * `ApplicationResponse` in the result is treated as NOT approved (R2.3).
 *
 * Loading / error discipline (R2.7, R2.8):
 *   - WHILE the list read is in-flight, `ready` is false and every lookup
 *     resolves to the not-approved default — surfaces MUST withhold approval
 *     filtering/counting and present a loading state.
 *   - IF the read fails, `ready` is false, `error` is set, and the map is
 *     cleared, so NO previously loaded Approved value is ever presented as
 *     current (never stale).
 */
export interface UseApprovalAccessListResult {
  /** True iff the applicant's `ApplicationResponse.submissionStatus === 'APPROVED'`; absent ⇒ false (R2.2, R2.3). */
  isApproved(applicantPersonId: string): boolean;
  /**
   * The Approval_SSOT `submissionStatus` for an applicant, or `null` when the
   * applicant has no `ApplicationResponse` in the list read (R2.3). The status
   * vocabulary is `ApplicationResponse.submissionStatus` (R2.6).
   */
  getStatus(applicantPersonId: string): SubmissionStatus | null;
  /**
   * True once the list read has completed successfully and approval
   * filtering/counting may proceed (R2.7). False while loading or on error
   * (R2.8) — the caller withholds filter/count and shows loading/error.
   */
  ready: boolean;
  loading: boolean;
  /** Distinct error indication — set when the list read fails/unavailable (R2.8). */
  error: string | null;
  /** The `competitionId` the list was read for. */
  competitionId: string;
  /** Re-run the list read (e.g. after a Director_Decision_Action; Task 6). */
  refetch(): void;
}

/**
 * Admin/director read mode of the Canonical_Approval_Reader (R2).
 *
 * @param competitionId The competition to read approval status for. When
 *   omitted, resolves the active-season operative year through the Season SSOT
 *   seam and derives the seeded competition id (mirroring `useAdminDashboard`).
 */
export function useApprovalAccessList(competitionId?: string): UseApprovalAccessListResult {
  const { operativeYear } = useActiveSeason();
  // Non-throwing tenant read: this list hook must not require an AuthProvider
  // (tenant-foundation Task 7). Absent → Idaho (back-compat).
  const activeTenantId = useActiveTenantId();
  const resolvedCompetitionId =
    competitionId ?? resolveActiveCompetitionId(operativeYear, activeTenantId);
  // A successful Director_Decision_Action bumps this; it re-runs the list read
  // so admin/director approval-gating surfaces reflect the new status within 5s
  // without a manual reload (Task 6, Req 5.5). The read clears the map and sets
  // `loading` up-front, so nothing stale is presented while the re-read is in
  // flight (Req 5.6) and a failed re-read surfaces an error with no stale
  // Approved values (Req 5.7 / R2.8).
  const invalidationVersion = useApprovalAccessInvalidation();

  // Keyed by applicantPersonId. Absent key ⇒ not approved (R2.3). The map is
  // cleared at the start of every read so a failed/in-flight read never
  // presents a previously loaded value as current (R2.7, R2.8).
  const [byApplicant, setByApplicant] = useState<Map<string, SubmissionStatus>>(new Map());
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchTick, setRefetchTick] = useState(0);

  const refetch = useCallback(() => {
    setRefetchTick((t) => t + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    // Clear any prior data up-front so nothing stale is exposed while the read
    // is in-flight or after it fails (R2.7, R2.8).
    setByApplicant(new Map());
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const summaries = await applicationFormClient.listResponses(resolvedCompetitionId);
        if (cancelled) return;
        const next = new Map<string, SubmissionStatus>();
        for (const s of summaries) {
          next.set(s.applicantPersonId, s.submissionStatus);
        }
        setByApplicant(next);
        setError(null);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        // Withhold filter/count, surface a distinct error, present no stale
        // Approved values — the map stays cleared (R2.8).
        console.error('[useApprovalAccessList] Failed to load approval status:', err);
        setByApplicant(new Map());
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedCompetitionId, refetchTick, invalidationVersion]);

  const ready = !loading && error === null;

  const getStatus = useCallback(
    (applicantPersonId: string): SubmissionStatus | null =>
      byApplicant.get(applicantPersonId) ?? null,
    [byApplicant],
  );

  const isApproved = useCallback(
    (applicantPersonId: string): boolean =>
      byApplicant.get(applicantPersonId) === 'APPROVED',
    [byApplicant],
  );

  return {
    isApproved,
    getStatus,
    ready,
    loading,
    error,
    competitionId: resolvedCompetitionId,
    refetch,
  };
}
